import React from 'react';
import { ArrowUpRight, BookOpen, Instagram, Sparkles, Play } from 'lucide-react';

const amazon = 'https://a.co/d/eGxJjHa';
const instagram = 'https://www.instagram.com/albert.moscoff.official/';
const ext = { target: '_blank', rel: 'noopener noreferrer' };

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-medium shadow-sm">
      <Sparkles size={12} /> {children}
    </span>
  );
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur bg-white/80 border-b border-black/5">
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <a href="#top" className="font-black tracking-tight text-lg">WIN</a>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="#inside" className="hover:opacity-60">Inside</a>
          <a href="#about" className="hover:opacity-60">About</a>
          <a href="#vibes" className="hover:opacity-60">Vibes</a>
          <a href="#faq" className="hover:opacity-60">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <a href={instagram} {...ext} className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2 text-sm hover:bg-black hover:text-white transition">
            <Instagram size={16} /> Instagram
          </a>
          <a href={amazon} {...ext} className="inline-flex items-center gap-2 rounded-xl bg-black text-white px-3 py-2 text-sm hover:opacity-90">
            Buy <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 soft-grid opacity-50 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-16 relative">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <Badge>Modern success playbook</Badge>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              WIN
              <span className="block text-xl sm:text-2xl lg:text-3xl font-semibold mt-3 text-black/80">
                The Laws and Principles of Enduring Success
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-black/70 max-w-xl">
              A practical framework for mastering your time, decisions, and power dynamics—so you can build a successful and fulfilling life.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={amazon} {...ext} className="inline-flex items-center gap-2 rounded-2xl bg-black text-white px-5 py-3 font-medium">
                Buy on Amazon <ArrowUpRight size={18} />
              </a>
              <a href="#inside" className="inline-flex items-center gap-2 rounded-2xl border border-black/10 px-5 py-3 font-medium hover:bg-black hover:text-white transition">
                <BookOpen size={18} /> See inside
              </a>
            </div>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl text-sm">
              <div className="rounded-2xl border border-black/10 p-3">
                <div className="font-semibold">Clear Frameworks</div>
                <div className="text-black/60">No fluff, direct principles</div>
              </div>
              <div className="rounded-2xl border border-black/10 p-3">
                <div className="font-semibold">Modern Edge</div>
                <div className="text-black/60">Power, timing, leverage</div>
              </div>
              <div className="rounded-2xl border border-black/10 p-3 col-span-2 sm:col-span-1">
                <div className="font-semibold">Practical</div>
                <div className="text-black/60">Built for real life</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-4 bg-white border border-black/10 rounded-2xl p-2 shadow-lg rotate-[-6deg] hidden sm:block">
              <img src="/images/book-flat.png" alt="WIN book detail" className="w-28 h-36 object-cover rounded-xl" />
            </div>
            <div className="rounded-3xl border border-black/10 p-2 shadow-xl bg-white">
              <div className="rounded-2xl overflow-hidden bg-neutral-50">
                <img src="/images/cover-hero.png" alt="WIN book cover" className="w-full h-auto object-cover" />
              </div>
            </div>
            <div className="absolute -bottom-5 -right-3 bg-black text-white rounded-2xl px-4 py-3 shadow-xl">
              <div className="text-xs opacity-80">Fastest way to reach Albert</div>
              <a href={instagram} {...ext} className="text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                Instagram DM <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Inside() {
  const cards = [
    ['Part I — Laws of Nature', ['The Law of Time', 'Sowing & Reaping', 'Attraction']],
    ['Part II — Behavioral Principles', ['Accurate Thoughts', 'Attitude', 'Dreams & Goals', 'Money']],
    ['How to use WIN', ['Daily systems', 'Decision filters', 'Long-term strategy']],
  ];
  return (
    <section id="inside" className="max-w-6xl mx-auto px-4 md:px-6 py-14">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight">Inside the book</h2>
          <p className="text-black/65 mt-2">Short chapters. Big ideas. Built to re-read and apply.</p>
        </div>
        <a href={amazon} {...ext} className="hidden md:inline-flex rounded-xl border border-black/10 px-4 py-2 text-sm hover:bg-black hover:text-white transition">Get the book</a>
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {cards.map(([title, items]) => (
          <div key={title} className="rounded-2xl border border-black/10 p-5 bg-white">
            <h3 className="font-semibold">{title}</h3>
            <ul className="mt-3 space-y-2 text-sm text-black/70">
              {items.map((x) => (
                <li key={x} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-black" />{x}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="max-w-6xl mx-auto px-4 md:px-6 py-14">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6 items-start">
        <div className="rounded-3xl border border-black/10 p-6 md:p-8 bg-white">
          <Badge>About Albert</Badge>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight">Builder. Strategist. Author.</h2>
          <p className="mt-4 text-black/70 leading-relaxed">
            Albert Moscoff writes about success, decision-making, and the hidden dynamics that shape outcomes. WIN combines practical frameworks with long-term thinking so readers can move with more clarity and confidence.
          </p>
          <p className="mt-3 text-black/70 leading-relaxed">
            If you want to connect about the book, content, or collaborations, the best place is Instagram.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={instagram} {...ext} className="inline-flex items-center gap-2 rounded-xl bg-black text-white px-4 py-2.5 text-sm"><Instagram size={16} /> DM on Instagram</a>
            <a href={amazon} {...ext} className="inline-flex items-center gap-2 rounded-xl border border-black/10 px-4 py-2.5 text-sm hover:bg-black hover:text-white transition">Buy WIN <ArrowUpRight size={16} /></a>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 rounded-2xl overflow-hidden border border-black/10">
            <img src="/images/author-main.png" alt="Albert Moscoff portrait" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-2xl overflow-hidden border border-black/10">
            <img src="/images/author-alt.png" alt="Albert with WIN" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-2xl overflow-hidden border border-black/10">
            <img src="/images/author-alt2.png" alt="Albert lifestyle portrait" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Vibes() {
  return (
    <section id="vibes" className="max-w-6xl mx-auto px-4 md:px-6 py-14">
      <div className="rounded-3xl border border-black/10 p-4 md:p-5 bg-white">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">WIN in the wild</h2>
            <p className="text-black/65 mt-1">A visual, social-first section for a younger audience.</p>
          </div>
          <a href={instagram} {...ext} className="hidden md:inline-flex items-center gap-2 text-sm rounded-xl border border-black/10 px-3 py-2 hover:bg-black hover:text-white transition">
            <Play size={15} /> Follow on Instagram
          </a>
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2 rounded-2xl overflow-hidden border border-black/10">
            <img src="/images/party.png" alt="Celebration with WIN book" className="w-full h-full object-cover" />
          </div>
          <div className="grid gap-3">
            <div className="rounded-2xl overflow-hidden border border-black/10">
              <img src="/images/reader.png" alt="Reader with WIN book" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden border border-black/10">
              <img src="/images/desk-shot.png" alt="Desk shot of WIN book" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    ['Who is WIN for?', 'Builders, entrepreneurs, and ambitious professionals who want practical principles they can actually use.'],
    ['Where can I buy it?', 'On Amazon. Use any Buy button on this page.'],
    ['How can I contact Albert?', 'Instagram DMs are the preferred way to connect.'],
    ['Will there be more content?', 'Yes—this site is designed to pair with social content, clips, and future updates.'],
  ];
  return (
    <section id="faq" className="max-w-6xl mx-auto px-4 md:px-6 py-14">
      <h2 className="text-2xl md:text-3xl font-black tracking-tight">FAQ</h2>
      <div className="mt-4 space-y-3">
        {faqs.map(([q, a]) => (
          <details key={q} className="group rounded-2xl border border-black/10 p-4 bg-white">
            <summary className="cursor-pointer font-medium list-none flex items-center justify-between">
              {q}
              <span className="text-black/40 group-open:rotate-45 transition">+</span>
            </summary>
            <p className="mt-2 text-sm text-black/70">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-black/5 mt-8">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-black/60">
        <div>© {new Date().getFullYear()} Albert Moscoff</div>
        <div className="flex items-center gap-4">
          <a href={instagram} {...ext} className="hover:text-black">Instagram</a>
          <a href={amazon} {...ext} className="hover:text-black">Amazon</a>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <main className="bg-white text-black">
      <Nav />
      <Hero />
      <Inside />
      <About />
      <Vibes />
      <FAQ />
      <Footer />
    </main>
  );
}
